import React, { useState, useEffect } from 'react';
import {
    CheckCircle, XCircle, RefreshCw, Eye,
    ArrowUpCircle, ArrowDownCircle, User,
    Clock, Download, ExternalLink, Image as ImageIcon,
    CreditCard, Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import adminService, { WalletRequest } from '../../services/admin/adminService';
import DataTable from '../../components/common/DataTable';
import { getImageUrl } from '../../utils/imageHelper';

const WalletRequests: React.FC = () => {
    const [requests, setRequests] = useState<WalletRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [statistics, setStatistics] = useState({
        totalRequests: 0,
        totalAmount: 0,
        pendingCount: 0,
        approvedCount: 0
    });

    // Modal states
    const [actionModal, setActionModal] = useState<{
        show: boolean;
        type: 'approve' | 'reject';
        request: WalletRequest | null;
        notes: string;
    }>({
        show: false,
        type: 'approve',
        request: null,
        notes: ''
    });

    const [screenshotModal, setScreenshotModal] = useState<{
        show: boolean;
        url: string | null;
    }>({
        show: false,
        url: null
    });

    const [detailsModal, setDetailsModal] = useState<{
        show: boolean;
        request: WalletRequest | null;
    }>({
        show: false,
        request: null
    });

    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async (showToast = false) => {
        try {
            if (showToast) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const [deposits, withdrawals] = await Promise.all([
                adminService.getPendingDeposits(),
                adminService.getPendingWithdrawals()
            ]);

            const allRequests = [...deposits, ...withdrawals].sort((a, b) => 
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );

            setStatistics({
                totalRequests: allRequests.length,
                totalAmount: allRequests.reduce((sum, req) => sum + Number(req.amount), 0),
                pendingCount: allRequests.filter(r => r.admin_check_status === 'pending').length,
                approvedCount: allRequests.filter(r => r.admin_check_status === 'approved').length
            });

            setRequests(allRequests);

            if (showToast) {
                toast.success(`Wallet requests refreshed!`);
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || `Failed to fetch data`);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleAction = async () => {
        if (!actionModal.request) return;

        try {
            setProcessing(true);

            // Prepare payload according to backend Joi schema requirements
            const payload: any = {
                transaction_id: actionModal.request.wallet_transaction_id,
                notes: actionModal.notes
            };

            if (actionModal.type === 'approve') {
                await adminService.approveWalletRequest(payload);
                toast.success('Request approved successfully!');
            } else {
                // For rejection, we can include the reason if the notes are populated
                payload.reason = actionModal.notes;
                await adminService.rejectWalletRequest(payload);
                toast.success('Request rejected successfully!');
            }

            setActionModal({ show: false, type: 'approve', request: null, notes: '' });
            fetchRequests();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Action failed');
        } finally {
            setProcessing(false);
        }
    };

    const openActionModal = (type: 'approve' | 'reject', request: WalletRequest) => {
        setActionModal({
            show: true,
            type,
            request,
            notes: ''
        });
    };

    const formatCurrency = (amount: string | number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(Number(amount));
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-secondary-900">Wallet Requests</h1>
                    <p className="text-secondary-600 mt-1">Approve or reject pending mirror wallet transactions</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => fetchRequests(true)}
                        disabled={refreshing}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                            <RefreshCw size={20} />
                        </div>
                        <div className="text-sm font-medium text-secondary-500">Total Requests</div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <div className="text-2xl font-bold text-secondary-900">{statistics.totalRequests}</div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-yellow-50 text-yellow-600 rounded-lg">
                            <Clock size={20} />
                        </div>
                        <div className="text-sm font-medium text-secondary-500">Pending Approvals</div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <div className="text-2xl font-bold text-secondary-900">{statistics.pendingCount}</div>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                            <CheckCircle size={20} />
                        </div>
                        <div className="text-sm font-medium text-secondary-500">Approved Today</div>
                    </div>
                    <div className="mt-3 flex items-baseline gap-2">
                        <div className="text-2xl font-bold text-secondary-900">{statistics.approvedCount}</div>
                    </div>
                </div>
            </div>

            {/* Requests Table */}
            <DataTable
                data={requests}
                loading={loading}
                searchable={true}
                searchPlaceholder="Search by user name or transaction ID..."
                searchFields={['full_name', 'email', 'reference_id']}
                filterable={true}
                filterOptions={[
                    { label: 'All Requests', value: 'all', field: 'admin_check_status' },
                    { label: 'Pending', value: 'pending', field: 'admin_check_status' },
                    { label: 'Approved', value: 'approved', field: 'admin_check_status' },
                    { label: 'Rejected', value: 'rejected', field: 'admin_check_status' },
                ]}
                defaultFilter="all"
                defaultPageSize={10}
                emptyMessage={`No wallet requests found`}
                emptyIcon={<Download size={64} className="text-secondary-300 mx-auto mb-4" />}
                columns={[
                    {
                        header: 'User Info',
                        accessor: (request) => (
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                    <User size={20} className="text-primary-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-secondary-900">
                                        {request.full_name}
                                    </h3>
                                    <p className="text-xs text-secondary-500">{request.email}</p>
                                </div>
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Type',
                        accessor: (request) => (
                            <div className={`text-xs font-bold px-2 py-1 rounded-full text-center ${
                                request.transaction_type === 'credit' 
                                    ? 'bg-green-50 text-green-700' 
                                    : 'bg-orange-50 text-orange-700'
                            }`}>
                                {request.transaction_type === 'credit' ? 'DEPOSIT' : 'WITHDRAWAL'}
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Amount',
                        accessor: (request) => (
                            <div className="text-sm font-bold text-secondary-900">
                                {formatCurrency(request.amount)}
                            </div>
                        ),
                        sortable: true,
                    },
                    {
                        header: 'Transaction Info',
                        accessor: (request) => (
                            <div className="text-sm">
                                {request.transaction_type === 'credit' ? (
                                    <>
                                        <p className="font-medium text-secondary-800">{request.bank_name || 'N/A'}</p>
                                        <p className="text-xs text-secondary-500">{request.payment_method_name}</p>
                                    </>
                                ) : (
                                    <p className="text-xs text-secondary-600 max-w-xs truncate" title={request.description || ''}>
                                        {request.description || 'N/A'}
                                    </p>
                                )}
                            </div>
                        ),
                    },
                    {
                        header: 'Reference/Date',
                        accessor: (request) => (
                            <div className="text-sm">
                                <p className="text-secondary-700 font-medium">#{request.reference_id || request.wallet_transaction_id}</p>
                                <div className="flex items-center gap-1 text-xs text-secondary-500 mt-0.5">
                                    <Clock size={12} />
                                    {new Date(request.created_at).toLocaleString()}
                                </div>
                            </div>
                        ),
                    },
                    {
                        header: 'Status',
                        accessor: (request) => (
                            <div className="space-y-1">
                                <span
                                    className={`inline-block px-3 py-1 text-xs rounded-full font-semibold capitalize ${request.admin_check_status === 'approved'
                                        ? 'bg-green-100 text-green-800'
                                        : request.admin_check_status === 'rejected'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                >
                                    {request.admin_check_status}
                                </span>
                                {request.admin_note && (
                                    <p className="text-[10px] text-secondary-400 max-w-[120px] truncate" title={request.admin_note}>
                                        {request.admin_note}
                                    </p>
                                )}
                            </div>
                        ),
                        align: 'center',
                        sortable: true,
                    },
                    {
                        header: 'Proof',
                        accessor: (request: WalletRequest) => (
                            request.payment_screenshot ? (
                                <button
                                    onClick={() => setScreenshotModal({ show: true, url: getImageUrl(request.payment_screenshot) || '' })}
                                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm transition-all bg-primary-50 px-3 py-1.5 rounded-lg"
                                >
                                    <ImageIcon size={16} />
                                    View Screen
                                </button>
                            ) : request.transaction_type === 'credit' ? (
                                <span className="text-xs text-gray-400">No Screenshot</span>
                            ) : (
                                <span className="text-xs text-gray-300">N/A</span>
                            )
                        ),
                        align: 'center' as const,
                    },
                ]}
                renderActions={(request) => (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setDetailsModal({ show: true, request });
                            }}
                            className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                            title="View Details"
                        >
                            <Eye size={18} />
                        </button>

                        {request.admin_check_status === 'pending' && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openActionModal('approve', request);
                                    }}
                                    className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                    title="Approve Request"
                                >
                                    <CheckCircle size={18} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openActionModal('reject', request);
                                    }}
                                    className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                    title="Reject Request"
                                >
                                    <XCircle size={18} />
                                </button>
                            </>
                        )}
                    </div>
                )}
            />

            {/* Action Modal (Approve/Reject) */}
            {actionModal.show && actionModal.request && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full shadow-2xl overflow-hidden">
                        <div className={`p-6 border-b flex items-center gap-3 ${actionModal.type === 'approve' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
                            }`}>
                            <div className={`p-2 rounded-full ${actionModal.type === 'approve' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                }`}>
                                {actionModal.type === 'approve' ? <CheckCircle size={24} /> : <XCircle size={24} />}
                            </div>
                            <h3 className="text-xl font-bold text-secondary-900">
                                {actionModal.type === 'approve' ? 'Approve Request' : 'Reject Request'}
                            </h3>
                        </div>

                        <div className="p-6">
                            <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-2 border border-gray-100">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">User:</span>
                                    <span className="font-semibold text-secondary-900">{actionModal.request.full_name}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Amount:</span>
                                    <span className="font-bold text-secondary-900">{formatCurrency(actionModal.request.amount)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Type:</span>
                                    <span className={`capitalize font-medium ${actionModal.request.transaction_type === 'credit' ? 'text-green-600' : 'text-danger-600'}`}>
                                        {actionModal.request.transaction_type === 'credit' ? 'Deposit' : 'Withdrawal'}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-secondary-700">
                                    {actionModal.type === 'approve' ? 'Approval Notes (Optional)' : 'Rejection Reason'}
                                </label>
                                <textarea
                                    required={actionModal.type === 'reject'}
                                    placeholder={actionModal.type === 'approve' ? "Payment verified from bank statement..." : "Reason for rejection..."}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none min-h-[100px]"
                                    value={actionModal.notes}
                                    onChange={(e) => setActionModal({ ...actionModal, notes: e.target.value })}
                                ></textarea>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={handleAction}
                                    disabled={processing || (actionModal.type === 'reject' && !actionModal.notes.trim())}
                                    className={`flex-1 px-4 py-3 text-white rounded-lg font-bold transition-all disabled:opacity-50 ${actionModal.type === 'approve' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                                        }`}
                                >
                                    {processing ? 'Processing...' : 'Confirm Action'}
                                </button>
                                <button
                                    onClick={() => setActionModal({ show: false, type: 'approve', request: null, notes: '' })}
                                    disabled={processing}
                                    className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Screenshot Modal */}
            {screenshotModal.show && screenshotModal.url && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[70] p-4" onClick={() => setScreenshotModal({ show: false, url: null })}>
                    <div className="relative max-w-4xl w-full bg-white rounded-xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-secondary-900">Payment Screenshot</h3>
                            <button
                                onClick={() => setScreenshotModal({ show: false, url: null })}
                                className="p-1 hover:bg-gray-200 rounded-full transition-all"
                            >
                                <XCircle size={24} className="text-gray-500" />
                            </button>
                        </div>
                        <div className="p-4 flex justify-center bg-secondary-900">
                            <img
                                src={screenshotModal.url}
                                alt="Payment Proof"
                                className="max-h-[70vh] w-auto object-contain rounded shadow-lg"
                            />
                        </div>
                        <div className="p-4 bg-gray-50 text-center">
                            <a
                                href={screenshotModal.url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-bold"
                            >
                                <ExternalLink size={18} />
                                Open Full Image
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {detailsModal.show && detailsModal.request && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setDetailsModal({ show: false, request: null })}>
                    <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 shrink-0">
                            <div>
                                <h3 className="text-xl font-bold text-secondary-900">Request Details</h3>
                                <p className="text-xs text-secondary-500 mt-1">Transaction ID: #{detailsModal.request.wallet_transaction_id}</p>
                            </div>
                            <button
                                onClick={() => setDetailsModal({ show: false, request: null })}
                                className="p-2 hover:bg-gray-100 rounded-full transition-all"
                            >
                                <XCircle size={22} className="text-gray-400" />
                            </button>
                        </div>

                        <div className="p-0 overflow-y-auto flex-1 min-h-0">
                            {/* Summary Banner */}
                            <div className={`p-6 flex items-center justify-between border-b ${detailsModal.request.transaction_type === 'credit' ? 'bg-green-50' : 'bg-red-50'
                                }`}>
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-xl ${detailsModal.request.transaction_type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                                        }`}>
                                        {detailsModal.request.transaction_type === 'credit' ? <ArrowDownCircle size={24} /> : <ArrowUpCircle size={24} />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-secondary-600">Request Amount</p>
                                        <p className={`text-2xl font-black ${detailsModal.request.transaction_type === 'credit' ? 'text-green-600' : 'text-danger-600'
                                            }`}>
                                            {formatCurrency(detailsModal.request.amount)}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize ${detailsModal.request.admin_check_status === 'approved' ? 'bg-green-500 text-white' :
                                        detailsModal.request.admin_check_status === 'rejected' ? 'bg-red-500 text-white' :
                                            'bg-yellow-500 text-white'
                                        }`}>
                                        {detailsModal.request.admin_check_status}
                                    </span>
                                    <p className="text-xs text-secondary-500 mt-2 font-medium">
                                        Last updated: {new Date(detailsModal.request.updated_at).toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* User Details Section */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-secondary-400 uppercase tracking-wider flex items-center gap-2">
                                        <User size={14} /> User Information
                                    </h4>
                                    <div className="p-4 bg-secondary-50 rounded-xl space-y-3">
                                        <div>
                                            <p className="text-[10px] text-secondary-400 uppercase">Full Name</p>
                                            <p className="text-sm font-bold text-secondary-900">{detailsModal.request.full_name}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-secondary-400 uppercase">Email Address</p>
                                            <p className="text-sm font-bold text-secondary-900">{detailsModal.request.email}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-secondary-400 uppercase">Phone Number</p>
                                            <p className="text-sm font-bold text-secondary-900">{detailsModal.request.mobile_number}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Details Section */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-secondary-400 uppercase tracking-wider flex items-center gap-2">
                                        <CreditCard size={14} /> Payment Information
                                    </h4>
                                    <div className="p-4 bg-secondary-50 rounded-xl space-y-3 font-medium">
                                        <div>
                                            <p className="text-[10px] text-secondary-400 uppercase">Type</p>
                                            <p className="text-sm capitalize font-bold text-secondary-900">{detailsModal.request.transaction_type === 'credit' ? 'Wallet Deposit' : 'Wallet Withdrawal'}</p>
                                        </div>
                                        {detailsModal.request.transaction_type === 'credit' ? (
                                            <>
                                                <div>
                                                    <p className="text-[10px] text-secondary-400 uppercase">Bank Name</p>
                                                    <p className="text-sm font-bold text-secondary-900">{detailsModal.request.bank_name || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-secondary-400 uppercase">Ref ID</p>
                                                    <p className="text-sm font-bold font-mono text-primary-600">{detailsModal.request.reference_id || 'N/A'}</p>
                                                </div>
                                            </>
                                        ) : (
                                            <div>
                                                <p className="text-[10px] text-secondary-400 uppercase">Withdrawal Notes</p>
                                                <p className="text-xs text-secondary-700 leading-relaxed italic">{detailsModal.request.description || 'No description provided'}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Proof / Screenshot Section */}
                                {detailsModal.request.transaction_type === 'credit' && (
                                    <div className="md:col-span-2 space-y-4 pt-2">
                                        <h4 className="text-xs font-bold text-secondary-400 uppercase tracking-wider flex items-center gap-2">
                                            <ImageIcon size={14} /> Transaction Proof
                                        </h4>
                                        {detailsModal.request.payment_screenshot ? (
                                            <div className="relative group cursor-zoom-in overflow-hidden rounded-2xl border-4 border-white shadow-soft"
                                                onClick={() => setScreenshotModal({ show: true, url: getImageUrl(detailsModal.request!.payment_screenshot) || '' })}>
                                                <img
                                                    src={getImageUrl(detailsModal.request.payment_screenshot) || ''}
                                                    alt="Payment Screen"
                                                    className="w-full h-48 object-cover transition-transform group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 flex items-center justify-center transition-all">
                                                    <span className="bg-white px-4 py-2 rounded-full text-xs font-bold shadow-lg opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all">View Full Proof</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-8 border-2 border-dashed border-gray-100 rounded-2xl text-center bg-gray-50/50">
                                                <ImageIcon size={32} className="text-gray-200 mx-auto mb-2" />
                                                <p className="text-xs text-secondary-400">No payment proof uploaded</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Admin Notes Section */}
                                {detailsModal.request.admin_note && (
                                    <div className="md:col-span-2 space-y-4 pt-2">
                                        <h4 className="text-xs font-bold text-secondary-400 uppercase tracking-wider flex items-center gap-2">
                                            <Info size={14} /> Admin Remarks
                                        </h4>
                                        <div className="p-4 bg-yellow-50/50 border border-yellow-100 rounded-xl">
                                            <p className="text-sm font-medium text-secondary-700 italic">"{detailsModal.request.admin_note}"</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex gap-3">
                            <button
                                onClick={() => setDetailsModal({ show: false, request: null })}
                                className="flex-1 px-4 py-2 bg-white border border-gray-300 text-secondary-700 rounded-lg hover:bg-gray-50 transition-colors font-bold"
                            >
                                Close View
                            </button>
                            {detailsModal.request.admin_check_status === 'pending' && (
                                <>
                                    <button
                                        onClick={() => {
                                            const req = detailsModal.request;
                                            setDetailsModal({ show: false, request: null });
                                            openActionModal('approve', req!);
                                        }}
                                        className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-bold shadow-lg shadow-green-100"
                                    >
                                        Approve now
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WalletRequests;
